import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import * as Yup from 'yup';
import {
  Box,
  Container,
  Button,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField as MuiSearchField,
  Paper,
  Card,
  CardContent,
  CardActionArea,
  Stepper,
  Step,
  StepLabel,
  Divider,
  Chip,
  Avatar,
  IconButton,
  InputAdornment,
  Alert,
  Fade,
  Stack,
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import BusinessIcon from '@mui/icons-material/Business';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import MuiPageHeader from '../../components/mui/MuiPageHeader';
import MuiCard from '../../components/mui/MuiCard';
import MuiForm from '../../components/mui/MuiForm';
import MuiTextField from '../../components/mui/MuiTextField';
import MuiSelect from '../../components/mui/MuiSelect';
import { postPayment, patchPayment, getPayments, getCustomers, getProviders, getBanks } from '../../helpers/api_helper';

// Payment type configurations
const PAYMENT_TYPES = [
  {
    value: 'CASH',
    icon: AttachMoneyIcon,
    color: 'success',
    description: 'Immediate cash transaction',
  },
  {
    value: 'CHECK',
    icon: CheckBoxIcon,
    color: 'warning',
    description: 'Payment via check with expiry date',
  },
  {
    value: 'CARD',
    icon: CreditCardIcon,
    color: 'info',
    description: 'Credit or debit card payment',
  },
  {
    value: 'BANK',
    icon: AccountBalanceIcon,
    color: 'primary',
    description: 'Bank transfer via IBAN',
  },
];

const MuiPaymentCreate = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  document.title = (isEditMode ? t('Edit Payment') : t('Create Payment')) + ' | flexflow - React Admin & Dashboard Template';

  // State for entity selection
  const [bound, setBound] = useState('Inbound');
  const [entityList, setEntityList] = useState([]);
  const [selectedEntity, setSelectedEntity] = useState(null);
  const [paymentType, setPaymentType] = useState('');
  const [banks, setBanks] = useState([]);
  const [initialValues, setInitialValues] = useState(null);
  const [loading, setLoading] = useState(isEditMode);

  // Modal state for entity selection
  const [entityModalOpen, setEntityModalOpen] = useState(false);
  const [entitySearchTerm, setEntitySearchTerm] = useState('');

  // Date states
  const [paymentDate, setPaymentDate] = useState(moment());
  const [expireDate, setExpireDate] = useState(null);

  // Ref to track if initial edit data has been loaded
  const initialEditLoadRef = useRef(false);

  // Calculate current step for stepper
  const getActiveStep = () => {
    if (!selectedEntity) return 0;
    if (!paymentType) return 1;
    return 2;
  };

  const steps = [
    t('Select Entity'),
    t('Payment Method'),
    t('Payment Details'),
  ];

  // Load existing payment data in edit mode
  useEffect(() => {
    if (isEditMode && id) {
      getPayments(id).then((res) => {
        if (res && res[0]) {
          const payment = res[0];
          initialEditLoadRef.current = true;
          setBound(payment.bound || 'Inbound');
          setPaymentType(payment.type || '');
          setPaymentDate(payment.date ? moment(payment.date) : moment());
          setExpireDate(payment.expire_date ? moment(payment.expire_date) : null);
          setSelectedEntity({ id: payment.entity_id, name: payment.entity_name });
          setInitialValues({
            amount: payment.ammount || '',
            series: payment.series || '',
            bankName: payment.bankname || '',
            issuer: payment.issuer || '',
            selectedBank: payment.bank_id || '',
            iban: payment.iban || '',
            receiptCode: payment.receipt_code || '',
          });
        }
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [id, isEditMode]);

  // Load banks on mount
  useEffect(() => {
    getBanks().then((res) => setBanks(res || [])).catch(() => {});
  }, []);

  // Load customers or providers based on bound
  useEffect(() => {
    if (bound === 'Inbound') {
      getCustomers().then((res) => {
        setEntityList(res || []);
      });
    } else if (bound === 'Outbound') {
      getProviders().then((res) => {
        setEntityList(res || []);
      });
    }
    if (initialEditLoadRef.current) {
      initialEditLoadRef.current = false;
    } else if (!loading) {
      setSelectedEntity(null);
    }
  }, [bound, loading]);

  // Filter entities based on search term
  const filteredEntities = entityList.filter((entity) => {
    const searchRegex = new RegExp(entitySearchTerm, 'i');
    return (
      searchRegex.test(entity.name) ||
      searchRegex.test(entity.description) ||
      searchRegex.test(entity.phone) ||
      searchRegex.test(entity.vat) ||
      searchRegex.test(entity.label)
    );
  });

  const handleEntitySelect = (entity) => {
    setSelectedEntity(entity);
    setEntityModalOpen(false);
    setEntitySearchTerm('');
  };

  const handleBoundChange = (newBound) => {
    setBound(newBound);
    setSelectedEntity(null);
    setPaymentType('');
  };

  const handlePaymentTypeChange = (newType) => {
    setPaymentType(newType);
  };

  // Validation schema based on payment type
  const getValidationSchema = () => {
    const baseSchema = {
      amount: Yup.number()
        .required(t('Amount is required'))
        .positive(t('Amount must be positive')),
      series: Yup.string(),
    };

    if (paymentType === 'CHECK') {
      return Yup.object({
        ...baseSchema,
        bankName: Yup.string().required(t('Bank name is required')),
        issuer: Yup.string().required(t('Issuer is required')),
      });
    }

    if (paymentType === 'BANK') {
      return Yup.object({
        ...baseSchema,
        selectedBank: Yup.string().required(t('Bank selection is required')),
        iban: Yup.string().required(t('IBAN is required')),
        receiptCode: Yup.string(),
      });
    }

    return Yup.object(baseSchema);
  };

  const getInitialValues = () => {
    if (isEditMode && initialValues) {
      return initialValues;
    }
    return {
      amount: '',
      series: '',
      bankName: '',
      issuer: '',
      selectedBank: '',
      iban: '',
      receiptCode: '',
    };
  };

  const handleSubmit = async (values, { setSubmitting, setStatus }) => {
    if (!selectedEntity) {
      setStatus({ error: t('Please select a customer/supplier') });
      setSubmitting(false);
      return;
    }

    if (!paymentType) {
      setStatus({ error: t('Please select a payment type') });
      setSubmitting(false);
      return;
    }

    const paymentData = {
      bound,
      type: paymentType,
      ammount: values.amount,
      series: values.series,
      date: paymentDate,
      entity_id: selectedEntity.id,
      entity_name: selectedEntity.name,
      refcode: '38.00.00',
      ...(paymentType === 'CHECK' && {
        bankname: values.bankName,
        expire_date: expireDate,
        issuer: values.issuer,
        refcode: '38.01.00',
      }),
      ...(paymentType === 'BANK' && {
        bankname: values.selectedBank,
        iban: values.iban,
        receipt_code: values.receiptCode,
        refcode: '38.02.00',
      }),
    };

    if (isEditMode) {
      paymentData.id = parseInt(id);
    }

    const payload = { resource: [paymentData] };

    try {
      const res = isEditMode
        ? await patchPayment(payload)
        : await postPayment(payload);
      if (res && res.error) {
        setStatus({ error: t(isEditMode ? 'Error updating payment' : 'Error creating payment') });
      } else if (res && res.resource && res.resource[0]) {
        navigate('/payment-overview/' + (isEditMode ? id : res.resource[0].id));
      }
    } catch (err) {
      setStatus({ error: t(isEditMode ? 'Error updating payment' : 'Error creating payment') });
    } finally {
      setSubmitting(false);
    }
  };

  const breadcrumbs = [
    { label: t('Payments'), path: '/payments' },
    { label: isEditMode ? t('Edit Payment') : t('Create Payment') },
  ];

  // Entity selection dialog columns
  const entityColumns = [
    { field: 'name', headerName: t('Name'), flex: 1, minWidth: 150 },
    { field: 'label', headerName: t('Label'), width: 120 },
    { field: 'phone', headerName: t('Phone'), width: 120 },
    { field: 'vat', headerName: t('VAT'), width: 120 },
  ];

  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ py: 3 }}>
        <Container maxWidth="lg">
          <MuiPageHeader
            title={isEditMode ? t('Edit Payment') : t('Create Payment')}
            subtitle={isEditMode ? t('Update payment details') : t('Record a new payment transaction')}
            breadcrumbs={breadcrumbs}
          />

          {/* Progress Stepper */}
          <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
            <Stepper activeStep={getActiveStep()} alternativeLabel>
              {steps.map((label, index) => (
                <Step key={label} completed={getActiveStep() > index}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>
          </Paper>

          <Grid container spacing={3}>
            {/* Main Form Column */}
            <Grid item xs={12} md={8}>
              {/* Step 1: Transaction Type & Entity Selection */}
              <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('Transaction Type')}
                </Typography>

                {/* Inbound/Outbound Toggle Cards */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6}>
                    <Card
                      variant={bound === 'Inbound' ? 'elevation' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        border: bound === 'Inbound' ? '2px solid' : '1px solid',
                        borderColor: bound === 'Inbound' ? 'success.main' : 'divider',
                        bgcolor: bound === 'Inbound' ? 'success.lighter' : 'background.paper',
                        '&:hover': {
                          borderColor: 'success.main',
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleBoundChange('Inbound')}
                    >
                      <CardActionArea sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: bound === 'Inbound' ? 'success.main' : 'grey.300', width: 48, height: 48 }}>
                            <ArrowDownwardIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {t('Inbound')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('Money received from customer')}
                            </Typography>
                          </Box>
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                  <Grid item xs={6}>
                    <Card
                      variant={bound === 'Outbound' ? 'elevation' : 'outlined'}
                      sx={{
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        border: bound === 'Outbound' ? '2px solid' : '1px solid',
                        borderColor: bound === 'Outbound' ? 'warning.main' : 'divider',
                        bgcolor: bound === 'Outbound' ? 'warning.lighter' : 'background.paper',
                        '&:hover': {
                          borderColor: 'warning.main',
                          transform: 'translateY(-2px)',
                          boxShadow: 2,
                        },
                      }}
                      onClick={() => handleBoundChange('Outbound')}
                    >
                      <CardActionArea sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: bound === 'Outbound' ? 'warning.main' : 'grey.300', width: 48, height: 48 }}>
                            <ArrowUpwardIcon />
                          </Avatar>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={600}>
                              {t('Outbound')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {t('Money paid to supplier')}
                            </Typography>
                          </Box>
                        </Box>
                      </CardActionArea>
                    </Card>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                {/* Entity Selection */}
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {bound === 'Inbound' ? t('Select Customer') : t('Select Supplier')}
                </Typography>

                {selectedEntity ? (
                  <Card variant="outlined" sx={{ bgcolor: 'grey.50' }}>
                    <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 2, '&:last-child': { pb: 2 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: bound === 'Inbound' ? 'success.main' : 'warning.main', width: 48, height: 48 }}>
                          {bound === 'Inbound' ? <PersonIcon /> : <BusinessIcon />}
                        </Avatar>
                        <Box>
                          <Typography variant="subtitle1" fontWeight={600}>
                            {selectedEntity.name}
                          </Typography>
                          <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
                            {selectedEntity.label && (
                              <Chip label={selectedEntity.label} size="small" variant="outlined" />
                            )}
                            {selectedEntity.phone && (
                              <Chip label={selectedEntity.phone} size="small" variant="outlined" />
                            )}
                          </Stack>
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={() => setEntityModalOpen(true)}
                      >
                        {t('Change')}
                      </Button>
                    </CardContent>
                  </Card>
                ) : (
                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    startIcon={<SearchIcon />}
                    onClick={() => setEntityModalOpen(true)}
                    sx={{ py: 2, borderStyle: 'dashed' }}
                  >
                    {bound === 'Inbound' ? t('Click to select a customer') : t('Click to select a supplier')}
                  </Button>
                )}
              </Paper>

              {/* Step 2: Payment Method Selection */}
              <Fade in={!!selectedEntity}>
                <Paper sx={{ p: 3, mb: 3, borderRadius: 2, display: selectedEntity ? 'block' : 'none' }}>
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    {t('Payment Method')}
                  </Typography>

                  <Grid container spacing={2}>
                    {PAYMENT_TYPES.map((type) => {
                      const Icon = type.icon;
                      const isSelected = paymentType === type.value;
                      return (
                        <Grid item xs={6} sm={3} key={type.value}>
                          <Card
                            variant={isSelected ? 'elevation' : 'outlined'}
                            sx={{
                              cursor: 'pointer',
                              transition: 'all 0.2s ease',
                              height: '100%',
                              border: isSelected ? '2px solid' : '1px solid',
                              borderColor: isSelected ? `${type.color}.main` : 'divider',
                              bgcolor: isSelected ? `${type.color}.lighter` : 'background.paper',
                              '&:hover': {
                                borderColor: `${type.color}.main`,
                                transform: 'translateY(-2px)',
                                boxShadow: 2,
                              },
                            }}
                            onClick={() => handlePaymentTypeChange(type.value)}
                          >
                            <CardActionArea sx={{ p: 2, height: '100%' }}>
                              <Box sx={{ textAlign: 'center' }}>
                                <Avatar
                                  sx={{
                                    bgcolor: isSelected ? `${type.color}.main` : 'grey.200',
                                    color: isSelected ? 'white' : 'grey.600',
                                    width: 48,
                                    height: 48,
                                    mx: 'auto',
                                    mb: 1,
                                  }}
                                >
                                  <Icon />
                                </Avatar>
                                <Typography variant="subtitle2" fontWeight={600}>
                                  {t(type.value.charAt(0) + type.value.slice(1).toLowerCase())}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                  {t(type.description)}
                                </Typography>
                              </Box>
                            </CardActionArea>
                          </Card>
                        </Grid>
                      );
                    })}
                  </Grid>
                </Paper>
              </Fade>

              {/* Step 3: Payment Details Form */}
              <Fade in={!!paymentType}>
                <Paper sx={{ p: 3, borderRadius: 2, display: paymentType ? 'block' : 'none' }}>
                  <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
                    {t('Payment Details')}
                  </Typography>

                  <MuiForm
                    initialValues={getInitialValues()}
                    validationSchema={getValidationSchema()}
                    onSubmit={handleSubmit}
                    enableReinitialize
                    actions={({ isSubmitting }) => (
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
                        <Button
                          variant="outlined"
                          onClick={() => navigate('/payments')}
                          disabled={isSubmitting}
                          size="large"
                        >
                          {t('Cancel')}
                        </Button>
                        <Button
                          type="submit"
                          variant="contained"
                          color="primary"
                          disabled={isSubmitting || !selectedEntity || !paymentType}
                          size="large"
                          sx={{ minWidth: 160 }}
                        >
                          {isSubmitting
                            ? (isEditMode ? t('Updating...') : t('Creating...'))
                            : (isEditMode ? t('Update Payment') : t('Create Payment'))}
                        </Button>
                      </Box>
                    )}
                  >
                    {({ values }) => (
                      <>
                        {/* Basic Details */}
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <MuiTextField
                              name="amount"
                              label={t('Amount')}
                              type="number"
                              required
                              InputProps={{
                                endAdornment: <InputAdornment position="end">EUR</InputAdornment>,
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <DatePicker
                              label={t('Payment Date')}
                              value={paymentDate}
                              onChange={(date) => setPaymentDate(date)}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  variant: 'outlined',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <MuiTextField
                              name="series"
                              label={t('Reference / Series')}
                              helperText={t('Optional reference number')}
                            />
                          </Grid>
                          {paymentType === 'CHECK' && (
                            <Grid item xs={12} sm={6}>
                              <DatePicker
                                label={t('Check Expiry Date')}
                                value={expireDate}
                                onChange={(date) => setExpireDate(date)}
                                slotProps={{
                                  textField: {
                                    fullWidth: true,
                                    variant: 'outlined',
                                  },
                                }}
                              />
                            </Grid>
                          )}
                        </Grid>

                        {/* CHECK specific fields */}
                        {paymentType === 'CHECK' && (
                          <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                              {t('Check Details')}
                            </Typography>
                            <Grid container spacing={3}>
                              <Grid item xs={12} sm={6}>
                                <MuiTextField
                                  name="bankName"
                                  label={t('Bank Name')}
                                  required
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <MuiTextField
                                  name="issuer"
                                  label={t('Issuer Name')}
                                  required
                                />
                              </Grid>
                            </Grid>
                          </>
                        )}

                        {/* BANK specific fields */}
                        {paymentType === 'BANK' && (
                          <>
                            <Divider sx={{ my: 3 }} />
                            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
                              {t('Bank Transfer Details')}
                            </Typography>
                            <Grid container spacing={3}>
                              <Grid item xs={12} sm={6}>
                                <MuiSelect
                                  name="selectedBank"
                                  label={t('Select Bank')}
                                  options={banks}
                                  getOptionLabel={(option) => option.name}
                                  getOptionValue={(option) => option.name}
                                  placeholder={t('Choose a bank')}
                                  required
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <MuiTextField
                                  name="iban"
                                  label={t('IBAN Account Number')}
                                  required
                                />
                              </Grid>
                              <Grid item xs={12} sm={6}>
                                <MuiTextField
                                  name="receiptCode"
                                  label={t('Receipt Code')}
                                  helperText={t('Optional transaction receipt code')}
                                />
                              </Grid>
                            </Grid>
                          </>
                        )}
                      </>
                    )}
                  </MuiForm>
                </Paper>
              </Fade>
            </Grid>

            {/* Summary Sidebar */}
            <Grid item xs={12} md={4}>
              <Paper sx={{ p: 3, borderRadius: 2, position: 'sticky', top: 100 }}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                  {t('Payment Summary')}
                </Typography>

                <Stack spacing={2}>
                  {/* Transaction Type */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('Type')}
                    </Typography>
                    <Chip
                      label={t(bound)}
                      color={bound === 'Inbound' ? 'success' : 'warning'}
                      size="small"
                      icon={bound === 'Inbound' ? <ArrowDownwardIcon /> : <ArrowUpwardIcon />}
                    />
                  </Box>

                  {/* Entity */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {bound === 'Inbound' ? t('Customer') : t('Supplier')}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {selectedEntity ? selectedEntity.name : '-'}
                    </Typography>
                  </Box>

                  {/* Payment Method */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('Method')}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {paymentType ? t(paymentType.charAt(0) + paymentType.slice(1).toLowerCase()) : '-'}
                    </Typography>
                  </Box>

                  {/* Date */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      {t('Date')}
                    </Typography>
                    <Typography variant="body2" fontWeight={500}>
                      {paymentDate ? paymentDate.format('DD/MM/YYYY') : '-'}
                    </Typography>
                  </Box>

                  <Divider />

                  {/* Completion Status */}
                  <Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {t('Completion')}
                    </Typography>
                    <Stack spacing={1}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon
                          fontSize="small"
                          color={selectedEntity ? 'success' : 'disabled'}
                        />
                        <Typography variant="body2" color={selectedEntity ? 'text.primary' : 'text.disabled'}>
                          {bound === 'Inbound' ? t('Customer selected') : t('Supplier selected')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon
                          fontSize="small"
                          color={paymentType ? 'success' : 'disabled'}
                        />
                        <Typography variant="body2" color={paymentType ? 'text.primary' : 'text.disabled'}>
                          {t('Payment method selected')}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CheckCircleIcon
                          fontSize="small"
                          color="disabled"
                        />
                        <Typography variant="body2" color="text.disabled">
                          {t('Details completed')}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          {/* Entity Selection Modal */}
          <Dialog
            open={entityModalOpen}
            onClose={() => setEntityModalOpen(false)}
            maxWidth="md"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
          >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: bound === 'Inbound' ? 'success.main' : 'warning.main' }}>
                  {bound === 'Inbound' ? <PersonIcon /> : <BusinessIcon />}
                </Avatar>
                <Typography variant="h6">
                  {bound === 'Inbound' ? t('Select Customer') : t('Select Supplier')}
                </Typography>
              </Box>
              <IconButton onClick={() => setEntityModalOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <MuiSearchField
                fullWidth
                variant="outlined"
                placeholder={t('Search by name, phone, VAT...')}
                value={entitySearchTerm}
                onChange={(e) => setEntitySearchTerm(e.target.value)}
                sx={{ mb: 2 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon color="action" />
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={{ height: 400 }}>
                <DataGrid
                  rows={filteredEntities}
                  columns={entityColumns}
                  pageSize={10}
                  rowsPerPageOptions={[10, 25]}
                  onRowClick={(params) => handleEntitySelect(params.row)}
                  disableSelectionOnClick
                  sx={{
                    border: 'none',
                    '& .MuiDataGrid-row': {
                      cursor: 'pointer',
                      '&:hover': {
                        bgcolor: 'action.hover',
                      },
                    },
                  }}
                  localeText={{
                    noRowsLabel: t('No results found'),
                  }}
                />
              </Box>
            </DialogContent>
            <DialogActions sx={{ p: 2 }}>
              <Button onClick={() => setEntityModalOpen(false)}>
                {t('Cancel')}
              </Button>
            </DialogActions>
          </Dialog>
        </Container>
      </Box>
    </LocalizationProvider>
  );
};

MuiPaymentCreate.propTypes = {
  history: PropTypes.object,
};

export default MuiPaymentCreate;
